<template>
  <div>
    <Head title="Clientes" />

    <!-- Barra de herramientas responsive -->
    <app-toolbar 
      name="Cliente" 
      namePlural="Clientes" 
      createHref="/clients/create" 
      :exportButtons="true"
      class="mb-6"
    >
      <template v-slot:search>
        <search-filter 
          v-model="form.search" 
          class="w-full lg:w-96" 
          @reset="reset"
        >
          <label class="block text-gray-700">Vendedor:</label>
          <select 
            v-model="form.vendor_id" 
            class="form-select mt-1 w-full"
          >
            <option :value="null">Todos los vendedores</option>
            <option 
              v-for="vendor in vendors" 
              :key="vendor.id" 
              :value="vendor.id"
            >
              {{vendor.first_name}} {{vendor.last_name}}
            </option>
          </select>
        </search-filter>
      </template>

      <template v-slot:exportArea>
        <div class="flex gap-2">
          <a 
            target="_blank" 
            href="/clientsReports/excel"
            class="btn-green flex items-center gap-2"
          >
            <i class="fa fa-file" />
            <span class="hidden sm:inline">Excel</span>
          </a>
          <a 
            target="_blank" 
            href="/clientsReports/pdf"
            class="btn-red flex items-center gap-2"
          >
            <i class="fa fa-print" />
            <span class="hidden sm:inline">PDF</span>
          </a>
        </div>
      </template>
    </app-toolbar>

    <!-- Tabla responsive -->
    <div class="bg-white rounded-xl shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left font-bold bg-gray-50">
              <th class="px-4 py-3">Empresa</th>
              <th class="px-4 py-3 hidden md:table-cell">RIF</th>
              <th class="px-4 py-3">Adeudado</th>
              <th class="px-4 py-3 hidden lg:table-cell">Ventas</th>
              <th class="px-4 py-3 hidden xl:table-cell">Email</th>
              <th class="px-4 py-3 hidden lg:table-cell">Tipo</th>
              <th class="px-4 py-3">Nombre</th>
              <th class="px-4 py-3 hidden md:table-cell">Dirección</th>
              <th class="px-4 py-3 hidden xl:table-cell">Supervisor</th>
              <th class="px-4 py-3 hidden lg:table-cell">Responsable</th>
              <th class="px-4 py-3 hidden md:table-cell">Teléfono</th>
              <th class="w-px"></th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="client in clients.data" 
              :key="client.id" 
              :class="[
                'hover:bg-gray-50 focus-within:bg-gray-50',
                client.balance > 0 ? 'bg-red-50' : ''
              ]"
            >
              <td class="border-t">
                <Link 
                  class="block px-4 py-3 focus:text-blue-600"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.companyName }}
                </Link>
              </td>
              <td class="border-t hidden md:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.identification }}
                </Link>
              </td>
              <td class="border-t">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  <span :class="client.balance > 0 ? 'text-red-600 font-medium' : ''">
                    ${{ formatNumber(client.balance) }}
                  </span>
                </Link>
              </td>
              <td class="border-t hidden lg:table-cell">
                <div class="px-4 py-3 flex flex-wrap gap-2">
                  <Link
                    v-for="order in client.orders" 
                    :key="order.id"
                    :href="`/orders/${order.id}`"
                    class="text-sm text-blue-600 hover:text-blue-800"
                  >
                    #{{ order.number_order }}
                  </Link>
                </div>
              </td>
              <td class="border-t hidden xl:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.email }}
                </Link>
              </td>
              <td class="border-t hidden lg:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.typeClient }}
                </Link>
              </td>
              <td class="border-t">
                <Link 
                  class="block px-4 py-3 flex items-center"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.name }}
                  <icon 
                    v-if="client.deleted_at" 
                    name="trash" 
                    class="ml-2 w-4 h-4 text-gray-400"
                  />
                </Link>
              </td>
              <td class="border-t hidden md:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.address }}
                </Link>
              </td>
              <td class="border-t hidden xl:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ getFullName(client.supervisor) }}
                </Link>
              </td>
              <td class="border-t hidden lg:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ getFullName(client.responsable) }}
                </Link>
              </td>
              <td class="border-t hidden md:table-cell">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  {{ client.phone }}
                </Link>
              </td>
              <td class="border-t w-px">
                <Link 
                  class="block px-4 py-3"
                  :href="`/clients/${client.id}`"
                >
                  <icon 
                    name="cheveron-right" 
                    class="w-5 h-5 text-gray-400"
                  />
                </Link>
              </td>
            </tr>
            <tr v-if="clients.data.length === 0">
              <td 
                class="px-4 py-8 text-center text-gray-500" 
                colspan="12"
              >
                No hay clientes que mostrar.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <pagination 
      class="mt-6" 
      :links="clients.meta.links" 
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import AppToolbar from '../../Shared/AppToolbar.vue'

export default {
  components: {
    Head,
    Icon, 
    Link,
    Pagination,
    SearchFilter,
    AppToolbar,
  },

  layout: Layout,

  props: {
    filters: Object,
    clients: Object,
    auth: Object,
    vendors: Object
  },

  data() {
    return {
      form: {
        search: this.filters.search,
        vendor_id: this.filters.vendor_id,
      }
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/clients', pickBy(this.form), { 
          preserveState: true 
        })
      }, 300),
    },
  },

  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },

    formatNumber(value) {
      return value?.toLocaleString('es-ES') ?? '0'
    },

    getFullName(user) {
      if (!user) return 'N/A'
      return `${user.first_name} ${user.last_name}`
    }
  },
}
</script>
